import { useMutation } from "@apollo/client";
import { ErrorBoundary } from "@sentry/react";
import { compact } from "lodash";
import { forwardRef, useRef, useState } from "react";
import {
  AskAiChatMessageFragmentFragment,
  ChatSessionMessageReactionFragmentFragment,
  ChatSessionReactionType,
  ChatbotRoleEnum,
  CreateChatMessageReactionMutation,
  CreateChatMessageReactionMutationVariables,
} from "types/graphql-schema";

import { successNotificationVar } from "@cache/cache";
import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import ErrorFallback from "@components/error-fallback/error-fallback";
import Loading from "@components/loading/loading";
import Modal from "@components/modal/modal";
import ModalTitle from "@components/modal/modal-title";
import { getTopicflowIcon } from "@components/topicflow-icon/topicflow-icon-helpers";
import { classNames } from "@helpers/css";
import { sanitizeMarkdown } from "@helpers/string";

import createChatMessageReactionMutation from "../graphql/create-chat-message-reaction-mutation";
import AskAIMessageContent from "./ask-ai-message-content";
import AskAIMessageMarkdown from "./ask-ai-message-markdown";
import AskAIToolCalls from "./ask-ai-tool-calls";

export const AskAIMessage = forwardRef(
  (
    {
      message,
      chatSessionId,
      reaction,
      isLastMessage,
      className,
      style,
    }: {
      message: AskAiChatMessageFragmentFragment;
      chatSessionId: number | null;
      reaction: ChatSessionMessageReactionFragmentFragment | null;
      isLastMessage: boolean;
      className?: string;
      style?: React.CSSProperties;
    },
    ref: React.Ref<HTMLDivElement>,
  ) => {
    const { isStreaming, content, imageUrls } = message;
    const isUserMessage = message.role === ChatbotRoleEnum.User;

    const [showCommentBox, setShowCommentBox] = useState(false);
    const [comment, setComment] = useState("");
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    const [createChatReaction, { loading: isSubmittingReaction }] = useMutation<
      CreateChatMessageReactionMutation,
      CreateChatMessageReactionMutationVariables
    >(createChatMessageReactionMutation);

    const handleClickThumbsUp = () => {
      if (!chatSessionId) {
        return;
      }
      createChatReaction({
        variables: {
          chatSessionId,
          messageId: message.id,
          reactionType: ChatSessionReactionType.ThumbsUp,
        },
        onCompleted: () => {
          successNotificationVar({ title: "Thank you for your feedback!" });
        },
      });
    };
    const handleClickThumbsDown = () => {
      if (!chatSessionId) {
        return;
      }
      setShowCommentBox(true);
    };

    const handleSubmitDislike = () => {
      if (!chatSessionId) {
        return;
      }
      createChatReaction({
        variables: {
          chatSessionId,
          messageId: message.id,
          reactionType: ChatSessionReactionType.ThumbsDown,
          comment: comment || undefined,
        },
        onCompleted: () => {
          successNotificationVar({ title: "Thank you for your feedback!" });
          setShowCommentBox(false);
          setComment("");
        },
      });
    };

    const isThumbsUp =
      !!reaction && reaction.reactionType === ChatSessionReactionType.ThumbsUp;
    const isThumbsDown =
      !!reaction &&
      reaction.reactionType === ChatSessionReactionType.ThumbsDown;

    const markdownContent =
      (isStreaming ? sanitizeMarkdown(content) : content) || "";
    const shouldRenderMarkdownContent = isUserMessage && !!markdownContent;

    const toolCalls = compact(message.toolCalls || []);
    const slashCommandMatch = isUserMessage
      ? markdownContent.match(/^\/([a-z0-9][a-z0-9-]*)(\s[\s\S]*)?$/)
      : null;
    const slashCommandName = slashCommandMatch?.[1] ?? null;
    const slashCommandRest = slashCommandMatch?.[2]?.trimStart() ?? "";

    return (
      <ErrorBoundary fallback={ErrorFallback}>
        <div
          className={classNames(
            !isLastMessage && "group/message-reaction",
            className,
          )}
          style={style}
          ref={ref}
        >
          {message.role === ChatbotRoleEnum.Assistant &&
            toolCalls.length > 0 && (
              <div className="mb-3">
                <AskAIToolCalls toolCalls={toolCalls} />
              </div>
            )}
          <div
            className={classNames(
              "w-full text-gray-700 flex",
              message.role === ChatbotRoleEnum.User
                ? "flex-col items-end gap-2 pl-12"
                : "flex-col gap-4",
            )}
            key={message.id}
            id={message.id}
          >
            {shouldRenderMarkdownContent && (
              <div
                className={classNames(
                  "prose prose-sm max-w-none",
                  "bg-white",
                  isUserMessage && "rounded-3xl py-2 px-4 bg-gray-100",
                  message.error && "text-red-600",
                )}
              >
                {slashCommandName && (
                  <span className="font-mono text-blue-link">
                    /{slashCommandName}
                    {slashCommandRest && " "}
                  </span>
                )}
                {slashCommandName ? (
                  slashCommandRest && (
                    <AskAIMessageMarkdown>
                      {slashCommandRest}
                    </AskAIMessageMarkdown>
                  )
                ) : (
                  <AskAIMessageMarkdown>{markdownContent}</AskAIMessageMarkdown>
                )}
              </div>
            )}
            {isUserMessage && imageUrls.length > 0 && (
              <div className="flex max-w-full justify-end gap-2">
                {imageUrls.map((imageUrl) => (
                  <a
                    key={imageUrl}
                    href={imageUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="block h-24 w-24 overflow-hidden rounded-md border border-gray-200"
                  >
                    <img
                      src={imageUrl}
                      alt=""
                      className="h-full w-full object-cover"
                    />
                  </a>
                ))}
              </div>
            )}
            {!isUserMessage && (
              <AskAIMessageContent
                content={content}
                isStreaming={!!isStreaming}
                chatSessionId={chatSessionId}
                messageId={message.id || "tmp"}
                canEditSuggestedAssessmentResponse={!isLastMessage}
              />
            )}
          </div>
          {message.role === ChatbotRoleEnum.Assistant && (
            <div
              className={classNames(
                "flex gap-1 items-center mt-2",
                !isLastMessage &&
                  !reaction &&
                  "opacity-0 group-hover/message-reaction:opacity-100",
              )}
            >
              <Button
                icon={getTopicflowIcon("thumbsUp")}
                onClick={handleClickThumbsUp}
                theme={buttonTheme.iconGray}
                disabled={isThumbsUp || !!isStreaming}
                className={classNames(
                  isThumbsUp && "opacity-100 text-gray-900",
                  isThumbsDown && "opacity-50 hover:opacity-100",
                )}
                aria-label="Thumbs up"
              />
              <Button
                icon={getTopicflowIcon("thumbsDown")}
                onClick={handleClickThumbsDown}
                theme={buttonTheme.iconGray}
                disabled={isThumbsDown || !!isStreaming}
                className={classNames(
                  isThumbsDown && "opacity-100 text-gray-900",
                  isThumbsUp && "opacity-50 hover:opacity-100",
                )}
                aria-label="Thumbs down"
              />
            </div>
          )}
          {message.role === ChatbotRoleEnum.Assistant && showCommentBox && (
            <Modal
              open={showCommentBox}
              onClose={() => {
                setShowCommentBox(false);
                setComment("");
              }}
              alignedTop
              initialFocus={textareaRef}
            >
              <div className="p-6 flex flex-col gap-4">
                <ModalTitle
                  onClose={() => {
                    setShowCommentBox(false);
                    setComment("");
                  }}
                  className="text-sm"
                >
                  What could be improved?
                </ModalTitle>
                <textarea
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  placeholder="Tell us what went wrong (optional)"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  rows={3}
                  ref={textareaRef}
                  aria-label="Feedback comment"
                />
                <div className="flex gap-2 justify-end items-center">
                  {isSubmittingReaction && <Loading mini size="5" />}
                  <Button
                    onClick={() => {
                      setShowCommentBox(false);
                      setComment("");
                    }}
                    theme={buttonTheme.default}
                    className="text-sm"
                    disabled={isSubmittingReaction}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSubmitDislike}
                    theme={buttonTheme.primary}
                    className="text-sm"
                    disabled={isSubmittingReaction}
                  >
                    Submit
                  </Button>
                </div>
              </div>
            </Modal>
          )}
        </div>
      </ErrorBoundary>
    );
  },
);

export default AskAIMessage;
